import React from 'react';
import { motion } from 'framer-motion';

const benefits = [
  { title: 'Spar opptil 35% på bomkostnader', desc: 'Intelligente algoritmer finner ruter med lavest total bomkostnad basert på din kjøretøytype og tidspunkt.', saving: '35%', color: '#FF5722' },
  { title: 'Reduser kjøretid med 20%', desc: 'Sanntids trafikkdata fra 4 000+ sensorer langs norske veier sikrer optimal fremkommelighet.', saving: '20%', color: '#004F98' },
  { title: 'CO₂-optimaliserte ruter', desc: 'Miljøvennlige alternativer beregner utslipp og foreslår grønnere kjøremønstre.', saving: '18%', color: '#2E7D32' },
];

const routePoints = [
  { x: 15, y: 80, label: 'Oslo S', type: 'start' },
  { x: 30, y: 55, label: 'E18', type: 'toll' },
  { x: 50, y: 40, label: 'Sandvika', type: 'waypoint' },
  { x: 65, y: 30, label: 'Bom', type: 'toll' },
  { x: 80, y: 20, label: 'Drammen', type: 'end' },
];

const RuteoptimaliseringSection = () => {
  return (
    <section id="ruteoptimalisering" style={{ padding: '80px 24px', background: 'rgba(255,255,255,0.01)', borderTop: '1px solid rgba(224,228,232,0.06)', borderBottom: '1px solid rgba(224,228,232,0.06)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '64px' }}>
          <div style={{ width: '4px', height: '56px', background: '#004F98', flexShrink: 0, marginTop: '4px' }} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#004F98' }}>Tjeneste 02</span>
              <div style={{ width: '60px', height: '1px', background: 'rgba(0,79,152,0.3)' }} />
            </div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '0.02em', marginBottom: '16px', lineHeight: 1.2 }}>Ruteoptimalisering</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.7, maxWidth: '560px' }}>
              Avansert AI-drevet ruteplanlegging som analyserer tusenvis av alternative ruter for å finne den optimale kombinasjonen av tid, kostnad og komfort.
            </p>
          </div>
        </div>

        {/* Asymmetrical layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '64px', alignItems: 'center', marginBottom: '64px' }} className="services-grid">
          {/* Route visualization */}
          <div style={{ position: 'relative' }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.1)', borderTop: '3px solid #004F98', borderRadius: '2px', padding: '32px', position: 'relative', overflow: 'hidden' }}>
              {/* Background grid */}
              <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,79,152,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,79,152,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />

              <div style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '20px' }}>Rutevisualisering — Live</div>

              {/* SVG route map */}
              <svg viewBox="0 0 100 100" style={{ width: '100%', height: '200px' }} preserveAspectRatio="none">
                {/* Optimal route */}
                <polyline points="15,80 30,55 50,40 65,30 80,20" fill="none" stroke="#004F98" strokeWidth="0.8" strokeDasharray="none" />
                {/* Alternative route (more expensive) */}
                <polyline points="15,80 25,70 40,65 60,50 75,35 80,20" fill="none" stroke="rgba(255,87,34,0.3)" strokeWidth="0.5" strokeDasharray="2,2" />
                {/* Route points */}
                {routePoints?.map(pt => (
                  <g key={pt?.label}>
                    <circle cx={pt?.x} cy={pt?.y} r={pt?.type === 'toll' ? 2 : 3}
                      fill={pt?.type === 'start' ? '#2E7D32' : pt?.type === 'end' ? '#004F98' : pt?.type === 'toll' ? '#FF5722' : 'rgba(224,228,232,0.4)'}
                      stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
                  </g>
                ))}
              </svg>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '20px', marginTop: '16px', flexWrap: 'wrap' }}>
                {[['#004F98', 'Optimal rute'], ['rgba(255,87,34,0.5)', 'Alternativ rute'], ['#FF5722', 'Bomstasjon']]?.map(([clr, lbl]) => (
                  <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '16px', height: '3px', background: clr, borderRadius: '2px' }} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--color-muted-foreground)' }}>{lbl}</span>
                  </div>
                ))}
              </div>

              {/* Savings badge */}
              <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,87,34,0.1)', border: '1px solid rgba(255,87,34,0.3)', borderRadius: '2px', padding: '8px 12px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: '#FF5722' }}>kr 84</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--color-muted-foreground)' }}>spart i dag</div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {benefits?.map((b, i) => (
              <motion.div key={b?.title} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ width: '56px', height: '56px', background: `${b?.color}15`, border: `1px solid ${b?.color}30`, borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: b?.color }}>{b?.saving}</span>
                </div>
                <div>
                  <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '6px', lineHeight: 1.2 }}>{b?.title}</h4>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.6, margin: 0 }}>{b?.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Implementation details */}
        <div style={{ background: 'rgba(0,79,152,0.04)', border: '1px solid rgba(0,79,152,0.12)', borderRadius: '2px', padding: '32px' }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '20px' }}>Implementeringsdetaljer</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }} className="impl-grid">
            {[
              ['REST API', 'Full API-tilgang med OAuth 2.0 autentisering og rate limiting på 1 000 req/min'],
              ['Webhook-støtte', 'Sanntidsvarsler ved ruteendringer, trafikkforstyrrelser og prisoppdateringer'],
              ['SDK-er', 'Offisielle SDK-er for JavaScript, Python, Java og .NET med full dokumentasjon'],
              ['SLA 99.9%', 'Garantert oppetid med redundante servere i Oslo og Bergen datasentre'],
            ]?.map(([title, desc]) => (
              <div key={title}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, color: '#004F98', marginBottom: '8px' }}>{title}</div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RuteoptimaliseringSection;
